import { useState } from 'react';
import { Star, Loader2, Send } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewsApi } from '../../lib/api';
import toast from 'react-hot-toast';

interface ReviewFormProps {
    productId: string;
    onSuccess?: () => void;
}

export function ReviewForm({ productId, onSuccess }: ReviewFormProps) {
    const [rating, setRating] = useState(5);
    const [hoverRating, setHoverRating] = useState(0);
    const [customerName, setCustomerName] = useState('');
    const [title, setTitle] = useState('');
    const [comment, setComment] = useState('');

    const queryClient = useQueryClient();

    const createReviewMutation = useMutation({
        mutationFn: () =>
            reviewsApi.create(productId, {
                customerName,
                rating,
                title: title.trim() || undefined,
                comment,
            }),
        onSuccess: () => {
            toast.success('Merci pour votre avis!', {
                icon: '⭐',
                style: { background: '#333', color: '#fff', borderRadius: '12px' },
            });
            // Reset form
            setRating(5);
            setCustomerName('');
            setTitle('');
            setComment('');
            // Invalidate queries to refetch reviews
            queryClient.invalidateQueries({ queryKey: ['reviews', productId] });
            queryClient.invalidateQueries({ queryKey: ['reviewStats', productId] });
            onSuccess?.();
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Erreur lors de l\'envoi de l\'avis');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!customerName.trim()) {
            toast.error('Veuillez entrer votre nom');
            return;
        }
        if (!comment.trim()) {
            toast.error('Veuillez entrer un commentaire');
            return;
        }
        createReviewMutation.mutate();
    };

    const isSubmitting = createReviewMutation.isPending;

    return (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-100 rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-gray-900">Laisser un avis</h3>

            {/* Star Rating Selector */}
            <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Votre note</label>
                <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            onClick={() => setRating(star)}
                            className="p-1 transition-transform hover:scale-110"
                        >
                            <Star
                                className={`w-7 h-7 transition-colors ${star <= (hoverRating || rating)
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-200 hover:text-yellow-200'
                                    }`}
                            />
                        </button>
                    ))}
                    <span className="ml-2 text-sm text-gray-500">
                        {rating === 5 && 'Excellent!'}
                        {rating === 4 && 'Très bien'}
                        {rating === 3 && 'Bien'}
                        {rating === 2 && 'Moyen'}
                        {rating === 1 && 'Décevant'}
                    </span>
                </div>
            </div>

            {/* Name Input */}
            <div className="space-y-2">
                <label htmlFor="customerName" className="text-sm font-medium text-gray-700">
                    Votre nom *
                </label>
                <input
                    id="customerName"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Ex: Ahmed B."
                    className="input"
                    required
                    maxLength={100}
                />
            </div>

            {/* Title Input (Optional) */}
            <div className="space-y-2">
                <label htmlFor="title" className="text-sm font-medium text-gray-700">
                    Titre (optionnel)
                </label>
                <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Résumez votre avis..."
                    className="input"
                    maxLength={100}
                />
            </div>

            {/* Comment Input */}
            <div className="space-y-2">
                <label htmlFor="comment" className="text-sm font-medium text-gray-700">
                    Votre avis *
                </label>
                <textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Partagez votre expérience avec ce produit..."
                    className="input min-h-[100px] resize-none"
                    required
                    maxLength={1000}
                />
            </div>

            {/* Submit Button */}
            <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center gap-2"
            >
                {isSubmitting ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Envoi en cours...
                    </>
                ) : (
                    <>
                        <Send className="w-4 h-4" />
                        Publier mon avis
                    </>
                )}
            </button>
        </form>
    );
}
